import Clickable from "@insite/mobius/Clickable";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { applyPropBuilder, injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import styled, { withTheme } from "styled-components";

// ✳︎ New/updated types & implementation
export interface TooltipPresentationProps {
    mergeCss?: boolean;
    cssOverrides?: {
        tooltipClickable?: StyledProp<TooltipProps>;
        tooltipBody?: StyledProp<TooltipProps>;
        tooltipContainer?: StyledProp<TooltipProps>;
        tooltipContainerInner?: StyledProp<TooltipProps>; // optional if you want an inner container
        tooltipWrapper?: StyledProp<TooltipProps>;
        tooltipArrow?: StyledProp<TooltipProps>;
        tooltipCloseButton?: StyledProp<TooltipProps>; // NEW
    };
    iconProps?: IconPresentationProps;
    /** Props that will be passed to the typography component rendered in the tooltip.
     * @themable */
    typographyProps?: TypographyPresentationProps;
}

export type TooltipComponentProps = MobiusStyledComponentProps<
    "span",
    {
        /** Component to be used as the trigger to optionally replace the default trigger icon. */
        triggerComponent?: React.ReactElement<{ tabIndex: number }>;
        /** The text that will appear in the tooltip (kept for backwards-compat). */
        text?: string;

        /** React content to render inside the tooltip popover. Takes precedence over `text`. */
        tooltipContent?: React.ReactNode;

        /** Called when tooltip opens */
        onOpen?: React.EventHandler<React.SyntheticEvent>;
        /** Called when tooltip closes */
        onClose?: React.EventHandler<React.SyntheticEvent>;
        /** Text to be used to describe the tooltip trigger. Used for screenreaders. Defaults to "more info" */
        triggerAltText?: string;

        /** How the tooltip is triggered. Defaults to 'click'. */
        triggerMode?: "click" | "hover" | "both";

        /** Hover open/close delays (ms). Used when triggerMode includes hover. */
        hoverOpenDelay?: number;
        hoverCloseDelay?: number;

        /** Show an optional close button in the tooltip body. */
        showCloseButton?: boolean;
        /** ARIA label for the close button (defaults to theme translation of "close"). */
        closeButtonAriaLabel?: string;
        /** Presentation props for the close icon (if you want to use an icon instead of ×). */
        closeIconProps?: IconPresentationProps;
    }
>;

export type TooltipProps = TooltipComponentProps & TooltipPresentationProps;

const ESC_KEY = 27;
const ENTER_KEY = 13;

const TooltipContainer = styled.div<InjectableCss<any>>`
    width: 220px;
    position: absolute;
    display: inline-flex;
    justify-content: center;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 11px;
    ${injectCss}
`;

const TooltipBody = styled.div<InjectableCss<any>>`
    position: relative; /* changed: so we can position the close button inside */
    display: inline;
    padding: 10px;
    text-align: center;
    min-width: 100px;
    border-radius: 5px;
    background: ${getColor("common.backgroundContrast")};
    color: ${getColor("common.background")};
    ${injectCss}
`;

const TooltipWrapper = styled.div<InjectableCss<any>>`
    position: relative;
    display: inline-block;
    ${injectCss}
`;

// necessary to use theme clickable styles and instance clickable overrides
const TooltipClickable = styled(Clickable)`
    padding: 0 1px 3px;
    ${injectCss}
`;

const TooltipArrow = styled.div<InjectableCss<any>>`
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 6px;
    border-style: solid;
    border-color: ${getColor("common.backgroundContrast")} transparent transparent transparent;
    ${injectCss}
`;

/** NEW: a themable close button inside the tooltip body */
const TooltipCloseButton = styled(Clickable)<InjectableCss<any>>`
    position: absolute;
    top: 6px;
    right: 6px;
    line-height: 1;
    ${injectCss}
`;

class Tooltip extends React.Component<TooltipProps> {
    state = { visible: false };

    element = React.createRef<HTMLDivElement>();
    tooltipElement = React.createRef<HTMLDivElement>();
    tooltipArrow = React.createRef<HTMLDivElement>();

    private openTimeout?: number;
    private closeTimeout?: number;

    componentDidMount() {
        document.addEventListener("mousedown", this.handleDocumentClick);
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleDocumentClick);
        if (this.openTimeout) {
            window.clearTimeout(this.openTimeout);
        }
        if (this.closeTimeout) {
            window.clearTimeout(this.closeTimeout);
        }
    }

    /** ---- Visibility helpers ---- */
    private setVisible = (visible: boolean, event?: React.SyntheticEvent | Event) => {
        if (this.state.visible === visible) {return;}

        this.setState({ visible }, () => {
            if (visible) {
                this.positionTooltip();
                this.props.onOpen && this.props.onOpen(event as any);
            } else {
                this.props.onClose && this.props.onClose(event as any);
            }
        });
    };

    private scheduleOpen = (delay = 0, event?: React.SyntheticEvent | Event) => {
        if (this.closeTimeout) {window.clearTimeout(this.closeTimeout);}
        if (this.openTimeout) {window.clearTimeout(this.openTimeout);}
        this.openTimeout = window.setTimeout(() => this.setVisible(true, event), delay);
    };

    private scheduleClose = (delay = 0, event?: React.SyntheticEvent | Event) => {
        if (this.openTimeout) {window.clearTimeout(this.openTimeout);}
        if (this.closeTimeout) {window.clearTimeout(this.closeTimeout);}
        this.closeTimeout = window.setTimeout(() => this.setVisible(false, event), delay);
    };

    /** ---- Original handlers updated for modes ---- */
    openTooltip = (event: React.SyntheticEvent) => {
        event.preventDefault();
        // Toggle like original behavior when click is allowed
        if (this.props.triggerMode === "hover") {return;} // click disabled in pure hover mode
        if (this.state.visible) {
            this.setVisible(false, event);
            setTimeout(() => this.setVisible(true, event), 100);
        } else {
            this.setVisible(true, event);
        }
    };

    closeTooltip = (event: React.SyntheticEvent | Event) => {
        if (this.state.visible) {
            this.setVisible(false, event);
        }
        
    };

    positionTooltip = () => {
        if (IS_SERVER_SIDE) {return;}
        const tooltip = this.tooltipElement.current;
        const hoverTrigger = this.element.current;
        const arrow = this.tooltipArrow.current;

        if (tooltip && hoverTrigger && arrow) {
            const toolTipRect = tooltip.getBoundingClientRect();
            const hoverTriggerRect = hoverTrigger.getBoundingClientRect();
            const arrowRect = arrow.getBoundingClientRect();

            if (toolTipRect.x < 0) {
                tooltip.style.left = `0`;
                tooltip.style.transform = `translateX(${-hoverTriggerRect.x}px)`;
                arrow.style.left = `0`;
                arrow.style.transform = `translateX(${hoverTriggerRect.x + arrowRect.width / 4}px)`;
            }
        }
    };

    handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.keyCode === ESC_KEY) {
            event.stopPropagation();
            this.closeTooltip(event);
        }
        if (event.keyCode === ENTER_KEY) {
            event.stopPropagation();
            this.openTooltip(event);
        }
    };

    handleDocumentClick = (event: MouseEvent) => {
        if (this.state.visible && !this.element.current?.contains(event?.target as Node)) {
            this.closeTooltip(event as any);
        }
    };

    /** ---- Hover support ---- */
    handleMouseEnter = (event: React.MouseEvent) => {
        const { triggerMode, hoverOpenDelay = 100 } = this.props;
        if (triggerMode === "hover" || triggerMode === "both") {
            this.scheduleOpen(hoverOpenDelay, event);
        }
    };

    handleMouseLeave = (event: React.MouseEvent) => {
        const { triggerMode, hoverCloseDelay = 100 } = this.props;
        if (triggerMode === "hover" || triggerMode === "both") {
            this.scheduleClose(hoverCloseDelay, event);
        }
    };

    handleTooltipMouseEnter = () => {
        // Cancel any pending close while hovering the tooltip body
        if (this.closeTimeout) {window.clearTimeout(this.closeTimeout);}
    };

    handleTooltipMouseLeave = (event: React.MouseEvent) => {
        const { triggerMode, hoverCloseDelay = 100 } = this.props;
        if (triggerMode === "hover" || triggerMode === "both") {
            this.scheduleClose(hoverCloseDelay, event);
        }
    };

    render() {
        const {
            text,
            tooltipContent,
            triggerComponent,
            triggerAltText,
            iconProps,
            mergeCss,
            showCloseButton,
            closeButtonAriaLabel,
            ...otherProps
        } = this.props;

        const hasText = typeof text === "string" && text.length > 0;
        const hasContent = tooltipContent !== undefined && tooltipContent !== null;

        if (!hasText && !hasContent) {
            return null;
        }

        const { spreadProps } = applyPropBuilder(this.props, { component: "tooltip" });
        const { applyStyledProp } = applyPropBuilder(this.props, { component: "tooltip" });
        const resolvedMergeCss = mergeCss ?? otherProps?.theme?.tooltip?.defaultProps?.mergeCss;

        let trigger;
        if (triggerComponent) {
            // preserve original behavior: clone to strip tabindex from passive icons
            trigger = React.cloneElement(triggerComponent, { tabIndex: -1 });
        } else {
            trigger = <IconMemo {...spreadProps("iconProps")} />;
        }

        const toolTipComponent = (
            <TooltipContainer
                data-id="tooltipContainer"
                css={applyStyledProp(["cssOverrides", "tooltipContainer"], resolvedMergeCss)}
                ref={this.tooltipElement}
                role="tooltip"
                aria-hidden={!this.state.visible}
                onMouseEnter={this.handleTooltipMouseEnter}
                onMouseLeave={this.handleTooltipMouseLeave}
                {...otherProps}
            >
                <TooltipArrow
                    ref={this.tooltipArrow}
                    css={applyStyledProp(["cssOverrides", "tooltipArrow"], resolvedMergeCss)}
                />
                <TooltipBody
                    data-id="tooltipBody"
                    css={applyStyledProp(["cssOverrides", "tooltipBody"], resolvedMergeCss)}
                >
                    {/* Optional close button */}
                    {showCloseButton ? (
                        <TooltipCloseButton
                            css={applyStyledProp(
                                ["cssOverrides", "tooltipCloseButton"],
                                resolvedMergeCss
                            )}
                            aria-label={closeButtonAriaLabel || this.props.theme!.translate("close")}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                this.closeTooltip(e);
                            }}
                        >
                            {/* Use provided icon if passed; fall back to × */}
                            {this.props.closeIconProps ? (
                                <IconMemo {...spreadProps("closeIconProps")} />
                            ) : (
                                <span aria-hidden>×</span>
                            )}
                        </TooltipCloseButton>
                    ) : null}

                    {/* Content precedence: content > text */}
                    {hasContent ? (
                        <div data-test-selector="toolTipContent">{tooltipContent}</div>
                    ) : (
                        <Typography as="p" {...spreadProps("typographyProps")} data-test-selector="toolTip">
                            {text}
                        </Typography>
                    )}
                </TooltipBody>
            </TooltipContainer>
        );

        const triggerMode = this.props.triggerMode ?? "click";

        return (
            <TooltipWrapper
                ref={this.element}
                css={applyStyledProp(["cssOverrides", "tooltipWrapper"], resolvedMergeCss)}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
            >
                <TooltipClickable
                    css={applyStyledProp(["cssOverrides", "tooltipClickable"], resolvedMergeCss)}
                    onClick={triggerMode !== "hover" ? this.openTooltip : undefined}
                    onKeyDown={this.handleKeyDown}
                    onBlur={this.closeTooltip}
                    data-test-selector="tooltip_OpenTooltip"
                    aria-expanded={this.state.visible}
                    aria-haspopup="true"
                >
                    {trigger}
                    <VisuallyHidden>{triggerAltText || this.props.theme!.translate("more info")}</VisuallyHidden>
                </TooltipClickable>

                {/* Keep polite live region for screen readers */}
                <div aria-live="polite" data-test-selector="tooltip_TooltipInfo">
                    {this.state.visible ? toolTipComponent : null}
                </div>
            </TooltipWrapper>
        );
    }
}
/** @component */
export default withTheme(Tooltip);